import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Linkedin, Pin, Search, Puzzle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { PLANS, formatNumber } from "@/lib/plans";

/**
 * "You're all set" — the first screen after sign-up.
 * From the Chrome extension (?source=extension) it explains how to use the
 * extension; from the website it points at Research. A ?plan= from the
 * pricing page adds a nudge to finish choosing that plan.
 */
export default function Welcome() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const fromExtension = params.get("source") === "extension";
  const plan = PLANS.find((p) => p.id === params.get("plan"));
  const first = (user?.name || "").trim().split(/\s+/)[0];
  const credits = Math.max(0, (user?.credits_total ?? 0) - (user?.credits_used ?? 0));

  return (
    <div className="relative flex h-full items-start justify-center overflow-y-auto px-6 py-10 md:items-center">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[680px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.12), transparent)" }} />
      <div className="relative w-full max-w-2xl space-y-6" data-testid="welcome-page">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-success-soft text-success">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <div>
          <h1 className="font-display text-[40px] leading-[1.1] tracking-[-0.03em]">
            You're all set{first ? `, ${first}` : ""}.
          </h1>
          <p className="mt-3 max-w-xl text-[17px] leading-7 text-ink-secondary">
            Your workspace is ready with <span className="font-semibold text-foreground">{formatNumber(credits)} free credits</span>.
            You're only charged a credit when we find the email or phone.
          </p>
        </div>

        {fromExtension ? (
          <ol className="grid gap-3" data-testid="welcome-extension-steps">
            <Step icon={Puzzle} title="Pin Inbound Labs to your toolbar">
              Click the puzzle icon at the top right of Chrome, then the pin next to Inbound Labs.
            </Step>
            <Step icon={Linkedin} title="Open any LinkedIn profile">
              Click the Inbound Labs button on the page, or the toolbar icon, to see their email and phone.
            </Step>
            <Step icon={Pin} title="Or visit any company website">
              The panel lists the decision-makers at that company.
            </Step>
          </ol>
        ) : (
          <ol className="grid gap-3">
            <Step icon={Search} title="Search 154M+ contacts">
              Filter by title, company, location and size. Search is free and unlimited.
            </Step>
            <Step icon={CheckCircle2} title="Reveal emails and direct phones">
              1 credit per email, 5 per direct phone. Never charged for a miss.
            </Step>
          </ol>
        )}

        {plan && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary bg-surface p-5 shadow-featured" data-testid="welcome-plan-nudge">
            <div>
              <div className="font-display text-lg">Finish setting up {plan.label}</div>
              <p className="text-sm text-muted-foreground">
                {formatNumber(plan.credits)} credits a month. First month ${plan.firstMonth}, then ${plan.monthly}/mo.
              </p>
            </div>
            <Button asChild><Link to={`/choose-plan?plan=${plan.id}`}>Continue to {plan.label}</Link></Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {fromExtension && (
            <Button asChild><a href="https://www.linkedin.com/feed/" target="_blank" rel="noreferrer">Open LinkedIn</a></Button>
          )}
          <Button variant={fromExtension ? "outline" : "default"} asChild data-testid="welcome-continue">
            <Link to="/research">Go to Research</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, children }) {
  return (
    <li className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5 shadow-card">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-violet-soft text-violet-text">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="font-semibold">{title}</div>
        <p className="mt-0.5 text-sm text-muted-foreground">{children}</p>
      </div>
    </li>
  );
}
